// HEIC/HEIF is an Apple proprietary image format, often used for photos/screenshots produced on Apple devices.
// on mobile, HEIC/HEIF files are rendered and viewable without any issues.
// on web, browsers do not support this proprietary format, aside from Apple's own Safari browser, and these images cannot be rendered.
//    so we must convert them to JPEG on the fly to make them renderable and viewable
import { useEffect, useState } from "react";

import isNil from "lodash/isNil";

import {
  isHeicContentType,
  resolveHeicUri,
} from "@/hooks/useProprietaryHeicFiles/resolveHeicUri.web";
import type {
  UseProprietaryHeicFiles,
  UseProprietaryHeicFilesProps,
} from "@/hooks/useProprietaryHeicFiles/types";
import useAppMonitor from "@/services/AppMonitor";

// Cache converted results since conversion is neither quick nor computationally cheap.
const HeicConversionCache = new Map<string, string>();

const useProprietaryHeicFiles = ({
  contentType,
  uri,
}: UseProprietaryHeicFilesProps): UseProprietaryHeicFiles => {
  const { captureException } = useAppMonitor();
  const [safeUri, setSafeUri] = useState<string | undefined>(uri);
  const [isConverting, setIsConverting] = useState<boolean>(false);

  useEffect(() => {
    let isCancelled = false;

    const resolveSafeUri = async () => {
      // no uri, nothing to do
      if (isNil(uri)) {
        setSafeUri(uri);
        return;
      }

      // if already in cache, return cached result
      const cachedUri = HeicConversionCache.get(uri);
      if (cachedUri) {
        setSafeUri(cachedUri);
        return;
      }

      // if not HEIC type file, pass-through URI unchanged
      const isNotHeic =
        !isHeicContentType(contentType) && !uri.startsWith("blob:");
      // while the heic-to library exposes a isHeic function, using it here would loss the benefit of lazy loading that large module
      if (isNotHeic) {
        setSafeUri(uri);
        return;
      }

      // has uri, is not cached, and is likely HEIC type file: then proceed with conversion
      setIsConverting(true);
      let resolvedUri = uri;
      try {
        resolvedUri = await resolveHeicUri({ contentType, uri });
        HeicConversionCache.set(uri, resolvedUri);
      } catch (error) {
        // on any error, falls back to original uri
        captureException(
          new Error("HEIC image conversion failed", { cause: error }),
        );
      }

      // across-async-invocations mutual exclusion provided on write by not calling setState if isCancelled
      if (isCancelled) return;
      setSafeUri(resolvedUri);
      setIsConverting(false);
    };

    void resolveSafeUri();
    return () => {
      isCancelled = true;
    };
  }, [captureException, contentType, uri]);

  return { isConverting, safeUri };
};

export default useProprietaryHeicFiles;
